import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, CalendarRange, Download, RefreshCcw } from 'lucide-angular';
import { McpReport, McpReportDetail } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { McpReportsService } from '../services/mcp-reports.service';

@Component({
  standalone: true,
  selector: 'app-mcp-reports-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './mcp-reports.page.html',
  styleUrl: './mcp-reports.page.scss',
})
export class McpReportsPage implements OnInit {
  private readonly service = inject(McpReportsService);
  private readonly toast = inject(ToastService);
  protected readonly productOptions = ['DAM', 'GDAM', 'RTM'] as const;

  protected readonly CalendarRangeIcon = CalendarRange;
  protected readonly DownloadIcon = Download;
  protected readonly RefreshCcwIcon = RefreshCcw;
  protected readonly loading = signal(false);
  protected readonly reports = signal<McpReport[]>([]);
  protected readonly selectedReport = signal<McpReportDetail | null>(null);
  protected readonly activeReportId = signal<string | null>(null);
  protected readonly fetching = signal(false);
  protected readonly refetchingById = signal<Record<string, boolean>>({});
  protected readonly form = signal({
    product: 'RTM',
    from_date: this.todayValue(),
    to_date: this.todayValue(),
  });

  protected readonly parsedSummary = computed(() => {
    try {
      const parsed = JSON.parse(this.selectedReport()?.summary_json || '[]') as unknown;
      if (Array.isArray(parsed)) {
        return { summary_rows: parsed as Array<Record<string, unknown>>, site_rows: [] as Array<Record<string, unknown>> };
      }
      if (parsed && typeof parsed === 'object') {
        const payload = parsed as { summary_rows?: Array<Record<string, unknown>>; site_rows?: Array<Record<string, unknown>> };
        return {
          summary_rows: Array.isArray(payload.summary_rows) ? payload.summary_rows : [],
          site_rows: Array.isArray(payload.site_rows) ? payload.site_rows : [],
        };
      }
      return { summary_rows: [] as Array<Record<string, unknown>>, site_rows: [] as Array<Record<string, unknown>> };
    } catch {
      return { summary_rows: [] as Array<Record<string, unknown>>, site_rows: [] as Array<Record<string, unknown>> };
    }
  });

  protected readonly selectedSummary = computed(() => this.parsedSummary().summary_rows);
  protected readonly siteRows = computed(() => this.parsedSummary().site_rows);
  protected readonly siteColumns = computed(() => {
    const columns: string[] = [];
    for (const row of this.siteRows()) {
      for (const key of Object.keys(row)) {
        if (!columns.includes(key)) {
          columns.push(key);
        }
      }
    }
    return columns;
  });

  ngOnInit(): void {
    this.loadReports();
  }

  protected updateField(field: 'product' | 'from_date' | 'to_date', value: string): void {
    this.form.update((current) => ({ ...current, [field]: value }));
    if (field === 'product') {
      this.activeReportId.set(null);
      this.selectedReport.set(null);
      this.loadReports();
    }
  }

  protected fetchReport(): void {
    const form = this.form();
    this.fetching.set(true);
    this.service.fetch(form).subscribe({
      next: (report) => {
        this.toast.show(`${report.product} MCP report fetched and stored`, '', 'success');
        this.fetching.set(false);
        this.selectedReport.set(report);
        this.activeReportId.set(report.id);
        this.loadReports();
      },
      error: (error) => {
        this.fetching.set(false);
        const detail = error?.error?.detail || 'Failed to fetch MCP report';
        this.toast.show(detail, '', 'danger');
      },
    });
  }

  protected selectReport(reportId: string): void {
    this.activeReportId.set(reportId);
    this.service.get(reportId).subscribe({
      next: (report) => this.selectedReport.set(report),
      error: () => this.toast.show('Failed to load report rows', '', 'danger'),
    });
  }

  protected downloadReport(report: McpReport): void {
    window.open(this.service.downloadUrl(report.id), '_blank');
  }

  protected refetchReport(report: McpReport): void {
    this.refetchingById.set({ ...this.refetchingById(), [report.id]: true });
    this.service.refetch(report.id).subscribe({
      next: (refreshed) => {
        this.toast.show(`${refreshed.product} MCP report re-downloaded successfully`, '', 'success');
        this.refetchingById.set({ ...this.refetchingById(), [report.id]: false });
        this.selectedReport.set(refreshed);
        this.activeReportId.set(refreshed.id);
        this.loadReports();
      },
      error: () => {
        this.refetchingById.set({ ...this.refetchingById(), [report.id]: false });
        this.toast.show('Failed to re-download report', '', 'danger');
      },
    });
  }

  protected isRefetching(reportId: string): boolean {
    return !!this.refetchingById()[reportId];
  }

  protected formatColumnLabel(value: string): string {
    return value
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private loadReports(): void {
    this.loading.set(true);
    this.service.list(this.form().product).subscribe({
      next: (rows) => {
        this.reports.set(rows || []);
        this.loading.set(false);
        const currentId = this.activeReportId() || rows?.[0]?.id || null;
        if (currentId) {
          this.selectReport(currentId);
        } else {
          this.selectedReport.set(null);
        }
      },
      error: () => {
        this.loading.set(false);
        this.reports.set([]);
        this.selectedReport.set(null);
        this.toast.show('Failed to load MCP reports', '', 'danger');
      },
    });
  }

  private todayValue(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }
}
